import { useCallback, useEffect, useMemo, useState } from "react";
import LocationAutocomplete from "../../components/ui/LocationAutocomplete";
import { getEventThumbnailUrl } from "../events/media/eventImageUrl";
import { listRemoteEvents } from "../events/services/events.api";
import { calculateTripCost, DEFAULT_TRIP_COST_SETTINGS, estimateRoadDistanceKm, estimateTolls, formatEuro } from "./tripCostCalculator";
import {
  preloadTripCalculatorShareImage,
  preloadTripCostShareImage,
  shareTripCalculator,
  shareTripCost,
} from "./tripCostShare";
import styles from "./TripCostCalculatorPage.module.css";
import layoutStyles from "./TripCostCalculatorLayout.module.css";
import MobileNumberWheelPicker from "./MobileNumberWheelPicker";

const POPULAR_EVENTS_LIMIT = 6;

function PopularEventsGlyph() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M13.3 2.4c.5 3.1-.7 4.6-2.1 6.1-1.2 1.3-2.5 2.7-2.5 5 0 1.7 1.1 3.2 2.7 3.8-.2-.5-.3-1-.3-1.6 0-1.6.9-2.7 2.1-3.8.2 1.8 1.3 2.8 2 3.8.5.7.7 1.3.7 2.1 1.7-1.1 2.7-3 2.7-5.1 0-3.8-2.2-7.4-5.3-10.3ZM12 22a8.5 8.5 0 0 1-8.5-8.5c0-3.6 2-6.9 5.3-9.2-.2 2.6.5 3.8 1.1 4.5.1-.5.4-1 .8-1.5 1.3-1.6 2.1-2.7 1.3-6.3 5.3 2.6 8.5 7 8.5 11.7A9.4 9.4 0 0 1 12 22Z" />
    </svg>
  );
}

function formatPopularEventDate(event) {
  if (event?.startsAt) {
    const date = new Date(event.startsAt);
    if (!Number.isNaN(date.getTime())) {
      return new Intl.DateTimeFormat("fr-FR", {
        weekday: "short",
        day: "numeric",
        month: "short",
      }).format(date);
    }
  }
  return event?.date || "Date à venir";
}

function readInitialValues() {
  const params = new URLSearchParams(window.location.search);
  return {
    fromLabel: params.get("from") || "",
    toLabel: params.get("to") || "",
    from: null,
    to: null,
    distanceKm: params.get("km") || "",
    consumptionLitersPer100Km: params.get("conso") || DEFAULT_TRIP_COST_SETTINGS.consumptionLitersPer100Km,
    fuelPricePerLiter: params.get("fuel") || DEFAULT_TRIP_COST_SETTINGS.fuelPricePerLiter,
    vehicleCostPerKm: params.get("vehicle") || DEFAULT_TRIP_COST_SETTINGS.vehicleCostPerKm,
    tolls: params.get("tolls") || DEFAULT_TRIP_COST_SETTINGS.tolls,
    occupants: params.get("people") || DEFAULT_TRIP_COST_SETTINGS.occupants,
    tripType: params.get("trip") === "roundtrip" ? "roundtrip" : "oneway",
  };
}

export default function TripCostCalculatorPage() {
  const [values, setValues] = useState(readInitialValues);
  const [notice, setNotice] = useState("");
  const [popularEvents, setPopularEvents] = useState([]);
  const [popularEventsStatus, setPopularEventsStatus] = useState("loading");
  const [numberWheel, setNumberWheel] = useState(null);
  const result = useMemo(
    () => calculateTripCost(values, { tripType: values.tripType }),
    [values]
  );
  const kilometerAllowance = Math.max(0, Number(values.vehicleCostPerKm) || 0);
  const isLowKilometerAllowance = kilometerAllowance < 0.1;

  useEffect(() => {
    document.documentElement.classList.add("karango-trip-cost-page");
    document.body.classList.add("karango-trip-cost-page");

    return () => {
      document.documentElement.classList.remove("karango-trip-cost-page");
      document.body.classList.remove("karango-trip-cost-page");
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    listRemoteEvents({
      limit: 200,
      signal: controller.signal,
      view: "discovery",
    })
      .then((events) => {
        const rankedEvents = [...events]
          .sort((left, right) => {
            const popularityDelta =
              (Number(right?.attendeesCount) || 0) - (Number(left?.attendeesCount) || 0);
            if (popularityDelta !== 0) return popularityDelta;
            return new Date(left?.startsAt || 0) - new Date(right?.startsAt || 0);
          })
          .slice(0, POPULAR_EVENTS_LIMIT);

        setPopularEvents(rankedEvents);
        setPopularEventsStatus("success");
      })
      .catch((error) => {
        if (error?.name !== "AbortError") setPopularEventsStatus("error");
      });

    return () => controller.abort();
  }, []);
  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      preloadTripCostShareImage({ ...values, pricePerPerson: result.suggestedPrice });
      preloadTripCalculatorShareImage();
    }, 500);
    return () => window.clearTimeout(timeoutId);
  }, [result.suggestedPrice, values]);

  const update = useCallback((key, value) => {
    setValues((current) => ({ ...current, [key]: value }));
  }, []);
  const openNumberWheel = useCallback((config) => setNumberWheel(config), []);
  const closeNumberWheel = useCallback(() => setNumberWheel(null), []);
  const confirmNumberWheel = useCallback((nextValue) => {
    if (!numberWheel) return;
    update(numberWheel.key, String(nextValue));
    closeNumberWheel();
  }, [closeNumberWheel, numberWheel, update]);
  const selectPlace = (side, suggestion) => {
    const [lng, lat] = suggestion.coordinates || [];
    const nextPoint = Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
    setValues((current) => {
      const next = { ...current, [`${side}Label`]: suggestion.address || suggestion.label || suggestion.name, [side]: nextPoint };
      const origin = side === "from" ? nextPoint : next.from;
      const destination = side === "to" ? nextPoint : next.to;
      const distance = estimateRoadDistanceKm(origin, destination);
      return distance ? { ...next, distanceKm: String(distance), tolls: String(estimateTolls(distance)) } : next;
    });
  };
  const handleShare = async () => {
    try {
      const status = await shareTripCost({ ...values, pricePerPerson: result.suggestedPrice });
      setNotice(status === "copied" ? "Lien du résultat copié." : status === "shared-image" ? "Résultat et image partagés." : "Partage du résultat ouvert.");
    } catch (error) {
      if (error?.name !== "AbortError") setNotice("Impossible de partager pour le moment.");
    }
  };
  const handleShareCalculator = async () => {
    try {
      const status = await shareTripCalculator();
      setNotice(status === "copied" ? "Lien du calculateur copié." : status === "shared-image" ? "Calculateur partagé." : "Partage du calculateur ouvert.");
    } catch (error) {
      if (error?.name !== "AbortError") setNotice("Impossible de partager le calculateur pour le moment.");
    }
  };

  return (
    <main className={`${styles.page} ${layoutStyles.scrollPage}`}>
      <header className={styles.header}><a href="/" className={styles.back}>← Karango</a><span>CALCULATEUR DE TRAJET</span><h1>Combien coûte vraiment ton trajet ?</h1><p>Une estimation claire à partager avec toutes les personnes dans la voiture.</p></header>
      <section className={styles.calculator}>
        <div className={styles.routeFields}>
          <label>Départ<LocationAutocomplete value={values.fromLabel} placeholder="Adresse de départ" countryCode="fr" onChange={(value) => update("fromLabel", value)} onSelect={(item) => selectPlace("from", item)} /></label>
          <span>→</span>
          <label>Arrivée<LocationAutocomplete value={values.toLabel} placeholder="Adresse d’arrivée" countryCode="fr" onChange={(value) => update("toLabel", value)} onSelect={(item) => selectPlace("to", item)} /></label>
        </div>
        <div className={styles.grid}>
          <label>Distance routière estimée<input className={layoutStyles.desktopNumberInput} type="number" min="1" step="1" value={values.distanceKm} onChange={(e) => update("distanceKm", e.target.value)} /><button className={layoutStyles.mobileNumberTrigger} type="button" onClick={() => openNumberWheel({ key: "distanceKm", title: "Distance routière", min: 1, max: 1000, step: 1, unit: "km" })}>{values.distanceKm || "Choisir"}<span>km</span></button><small>km · ajustable après vérification GPS</small></label>
          <label>Personnes dans la voiture<input className={layoutStyles.desktopNumberInput} type="number" min="1" max="9" step="1" value={values.occupants} onChange={(e) => update("occupants", e.target.value)} /><button className={layoutStyles.mobileNumberTrigger} type="button" onClick={() => openNumberWheel({ key: "occupants", title: "Personnes dans la voiture", min: 1, max: 9, step: 1, unit: "personnes" })}>{values.occupants}<span>personnes</span></button><small>conducteur compris</small></label>
          <label>Consommation<input className={layoutStyles.desktopNumberInput} type="number" min="1" max="30" step="0.1" value={values.consumptionLitersPer100Km} onChange={(e) => update("consumptionLitersPer100Km", e.target.value)} /><button className={layoutStyles.mobileNumberTrigger} type="button" onClick={() => openNumberWheel({ key: "consumptionLitersPer100Km", title: "Consommation", min: 1, max: 30, step: 0.1, unit: "L / 100 km" })}>{values.consumptionLitersPer100Km}<span>L / 100</span></button><small>L / 100 km</small></label>
          <label>Prix du carburant<input className={layoutStyles.desktopNumberInput} type="number" min="0" step="0.01" value={values.fuelPricePerLiter} onChange={(e) => update("fuelPricePerLiter", e.target.value)} /><button className={layoutStyles.mobileNumberTrigger} type="button" onClick={() => openNumberWheel({ key: "fuelPricePerLiter", title: "Prix du carburant", min: 0.5, max: 4, step: 0.01, unit: "€ / litre" })}>{values.fuelPricePerLiter}<span>€ / L</span></button><small>€ / litre</small></label>
          <label className={`${styles.allowanceField} ${layoutStyles.allowanceLayout} ${isLowKilometerAllowance ? styles.allowanceFieldLow : ""}`}>
            <span className={styles.allowanceLabelRow}><span>Indemnisation kilométrique</span><strong>{kilometerAllowance.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/km</strong></span>
            <input className={`${styles.allowanceSlider} ${layoutStyles.sliderComfort}`} type="range" min="0" max="0.5" step="0.01" value={kilometerAllowance} onChange={(e) => update("vehicleCostPerKm", e.target.value)} aria-label="Indemnisation kilométrique par kilomètre" />
            <span className={styles.allowanceScale}><span>0 €</span><span>0,10 € conseillé</span><span>0,50 €</span></span>
            <small>{isLowKilometerAllowance ? "Indemnisation faible : moins de 0,10 € par kilomètre." : "0,10 € par kilomètre conseillé par défaut, montant ajustable."}</small>
          </label>
          <label>Péages (aller){values.tripType === "roundtrip" ? <em className={styles.tollsHintRoundTrip}>compté aller-retour</em> : null}<input className={layoutStyles.desktopNumberInput} type="number" min="0" step="0.5" value={values.tolls} onChange={(e) => update("tolls", e.target.value)} /><button className={layoutStyles.mobileNumberTrigger} type="button" onClick={() => openNumberWheel({ key: "tolls", title: "Péages (aller)", min: 0, max: 200, step: 0.5, unit: "€" })}>{values.tolls}<span>€</span></button><small>€ pour le trajet aller uniquement</small></label>
        </div>
        <div className={styles.tripTypeToggle} role="group" aria-label="Type de trajet">
          <button type="button" className={`${styles.tripTypeBtn} ${values.tripType !== "roundtrip" ? styles.tripTypeBtnActive : ""}`} aria-pressed={values.tripType !== "roundtrip"} onClick={() => update("tripType", "oneway")}>Aller simple</button>
          <button type="button" className={`${styles.tripTypeBtn} ${values.tripType === "roundtrip" ? styles.tripTypeBtnActive : ""}`} aria-pressed={values.tripType === "roundtrip"} onClick={() => update("tripType", "roundtrip")}>Aller-retour</button>
        </div>
        <div className={`${styles.result} ${layoutStyles.resultCard}`}>
          <div className={layoutStyles.resultHero}>
            <span className={layoutStyles.resultEyebrow}>PARTICIPATION CONSEILLÉE{values.tripType === "roundtrip" ? " · ALLER-RETOUR" : ""}</span>
            <div className={layoutStyles.resultAmount}>
              <strong>{formatEuro(result.suggestedPrice)}</strong>
              <span>par personne</span>
            </div>
            <p>{result.occupants} personne{result.occupants > 1 ? "s" : ""} dans la voiture · {result.distanceKm} km {values.tripType === "roundtrip" ? "aller-retour" : "aller simple"}</p>
          </div>
          <div className={layoutStyles.summaryPanel}>
            <span className={layoutStyles.summaryHeading}>Détail du trajet</span>
            <dl className={layoutStyles.summaryList}>
              <div className={layoutStyles.summaryLine}><dt>Carburant</dt><dd>{formatEuro(result.fuelCost)}</dd></div>
              <div className={layoutStyles.summaryLine}><dt>Indemnité kilométrique</dt><dd>{formatEuro(result.vehicleCost)}</dd></div>
              <div className={layoutStyles.summaryLine}><dt>Péages</dt><dd>{formatEuro(result.tolls)}</dd></div>
              <div className={`${layoutStyles.summaryLine} ${layoutStyles.summaryTotal}`}><dt>Total du trajet</dt><dd>{formatEuro(result.totalCost)}</dd></div>
            </dl>
          </div>
        </div>
        <p className={`${styles.coefficientExplanation} ${isLowKilometerAllowance ? styles.coefficientExplanationLow : ""}`}>Le coefficient de <strong>{kilometerAllowance.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/km</strong> ajoute une participation à l’entretien du véhicule. Tu peux l’augmenter, le réduire ou le ramener à 0 €.</p>
        <details className={styles.contributionBenefits}>
          <summary>Pourquoi contribuer au trajet ?</summary>
          <div className={styles.contributionBenefitsBody}>
            <p>Le passager profite du trajet sans supporter toutes les contraintes de la conduite.</p>
            <ul>
              <li><strong>Plus de confort :</strong> se reposer, discuter ou dormir lorsque les conditions le permettent.</li>
              <li><strong>Moins de charge mentale :</strong> pas d’itinéraire, de circulation ni de stationnement à gérer.</li>
              <li><strong>Pas de voiture mobilisée :</strong> carburant, entretien et kilométrage restent à la charge du véhicule utilisé.</li>
              <li><strong>Responsabilité du conducteur :</strong> vigilance, sécurité du trajet et gestion du véhicule.</li>
            </ul>
          </div>
        </details>
        <div className={layoutStyles.shareActions}>
          <button className={styles.share} type="button" onClick={handleShare}>↗ Partager mon résultat</button>
          <button className={layoutStyles.shareCalculator} type="button" onClick={handleShareCalculator}>Partager le calculateur</button>
        </div>
        {notice ? <p className={styles.notice} role="status">{notice}</p> : null}
        <p className={styles.disclaimer}>Estimation indicative. La distance est approchée à partir des adresses et reste modifiable.</p>
      </section>
      <MobileNumberWheelPicker open={Boolean(numberWheel)} {...numberWheel} value={numberWheel ? values[numberWheel.key] : 0} onCancel={closeNumberWheel} onConfirm={confirmNumberWheel} />

      <section className={styles.popularEvents} aria-labelledby="popular-events-title">
        <div className={styles.popularEventsHeading}>
          <span className={styles.popularEventsLogo}><PopularEventsGlyph /></span>
          <div>
            <h2 id="popular-events-title">Les sorties les plus populaires</h2>
          </div>
        </div>

        {popularEventsStatus === "loading" ? (
          <p className={styles.popularEventsState} role="status">Chargement des sorties populaires…</p>
        ) : null}
        {popularEventsStatus === "error" ? (
          <p className={styles.popularEventsState}>Les sorties sont momentanément indisponibles.</p>
        ) : null}
        {popularEventsStatus === "success" && popularEvents.length === 0 ? (
          <p className={styles.popularEventsState}>Aucune sortie à venir pour le moment.</p>
        ) : null}

        {popularEvents.length > 0 ? (
          <div className={styles.popularEventsGrid}>
            {popularEvents.map((event, index) => {
              const imageUrl = getEventThumbnailUrl(event);
              const attendeeCount = Math.max(0, Number(event?.attendeesCount) || 0);
              return (
                <a className={styles.popularEventCard} href={`/events/${encodeURIComponent(event.id)}`} key={event.id}>
                  <div className={styles.popularEventMedia}>
                    {imageUrl ? <img src={imageUrl} alt="" loading="lazy" decoding="async" /> : <span aria-hidden="true">K</span>}
                    <span className={styles.popularEventRank}>#{index + 1}</span>
                  </div>
                  <div className={styles.popularEventBody}>
                    <span className={styles.popularEventMeta}>{formatPopularEventDate(event)} · {event?.location?.city || "Lieu à venir"}</span>
                    <strong>{event.title}</strong>
                    <span className={styles.popularEventAttendance}>{attendeeCount} participant{attendeeCount > 1 ? "s" : ""}</span>
                  </div>
                </a>
              );
            })}
          </div>
        ) : null}
      </section>
    </main>
  );
}
